import React, { useEffect } from 'react';
import { useFieldArray, useForm } from "react-hook-form";
import { IPost } from '@core/interfaces/IPost';
import Input from "../../../shared/Input/Input";
import Label from "../../../components/Label/Label";
import Textarea from "../../../shared/Textarea/Textarea";
import Editor from "../../../@core/components/Editor";
import ButtonPrimary from "../../../shared/Button/ButtonPrimary";
import { useBlog } from "../../../hooks/collections/useBlog";
import { useNavigate } from "react-router-dom";
import { useAlert } from "../../../@shared/hooks/useAlert";
import { useLoader } from "../../../@shared/hooks/useLoader";
import ButtonSecondary from "../../../shared/Button/ButtonSecondary";

export const BlogForm = ({ post }: { post: IPost }) => {
  const { updatePost, createPost, deletePost } = useBlog({ loadData: false });
  const navigate = useNavigate();
  const alert = useAlert();
  const loader = useLoader();
  const { control, watch, formState: { errors }, handleSubmit, register, setValue } = useForm({
    defaultValues: {
      title: post?.title || '',
      description: post?.description || '',
      coverURL: post?.coverURL || '',
      contents: post?.contents?.length ? post.contents : [{ subtitle: '', content: '' }]
    }
  });

  const imageWatch = watch("coverURL");
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'contents'
  });

  const submit = async (formData: any) => {
    loader.start();
    try {
      post ? await updatePost(post.id, formData) : await createPost(formData);
      navigate('/account-company-blog');
      alert.toastSuccess("Post saved successfully!");
    } catch (e) {
      alert.toastError("An error occurred while saving the post.");
      console.log(e);
    } finally {
      loader.stop();
    }
  };

  const handleTrashPost = async () => {
    try {
      if (!await alert.confirm("Are you sure you want to delete this post?")) return;
      loader.start();
      if (post) await deletePost(post.id);
      alert.toastSuccess("Post deleted successfully!");
      navigate('/account-company-blog');
    } catch (e) {
      console.log(e);
      alert.toastError("An error occurred while deleting the post.");
    } finally {
      loader.stop();
    }
  };

  useEffect(() => {
    if (fields.length === 0) append({ subtitle: '', content: '' });
  }, [fields.length, append]);

  return (
    <div>
      <form onSubmit={handleSubmit(submit)} className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <aside>
          <div>
            <Label>Title*</Label>
            <Input {...register("title", { required: true })}/>
            {errors.title && <span className="text-red-500 text-sm">This field is required</span>}
          </div>

          <div>
            <Label>Description</Label>
            <Textarea {...register("description", { required: true })}/>
            {errors.description && <span className="text-red-500 text-sm">This field is required</span>}
          </div>

          <div>
            <Label>Cover</Label>
            <div className="dropzone mt-2 border border-dashed p-4 text-center relative">
              <input
                type="file"
                className="absolute inset-0 opacity-0 cursor-pointer"
                {...register("coverURL")}
              />
              <p>Drop files here or click to upload</p>
            </div>
            {imageWatch && (
              <div className="mt-2">
                <img
                  src={typeof imageWatch === 'string' ? imageWatch : URL.createObjectURL(imageWatch[0])}
                  alt="Preview"
                  className="size-32 object-cover mx-auto"
                />
              </div>
            )}
          </div>

          <div className="mt-4 flex flex-col gap-2">
            <ButtonPrimary className={"flex-1"} type="submit">Save Blog Post</ButtonPrimary>
            <ButtonSecondary className={"flex-1"} type={"button"} onClick={handleTrashPost}>Trash post</ButtonSecondary>
          </div>
        </aside>

        <section className="space-y-4">
          <Label>Content Sections</Label>
          {fields.map((field, index) => (
            <div key={field.id} className="border dark:border-neutral-700 p-4 rounded-lg mb-4">
              <div className="flex justify-between items-center mb-2">
                <h3 className="font-medium">Section {index + 1}</h3>
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className={`text-red-500 hover:text-red-700 ${fields.length <= 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                  disabled={fields.length <= 1}
                >
                  Remove
                </button>
              </div>
              <div className="mb-3">
                <Label>Subtitle</Label>
                <Input {...register(`contents.${index}.subtitle`, { required: true })} />
                {errors.contents?.[index]?.subtitle &&
                  <span className="text-red-500 text-sm">Subtitle is required</span>}
              </div>
              <div>
                <Label>Content</Label>
                <Editor
                  name={`contents.${index}.content`}
                  defaultValue={field.content || ''}
                  onChange={(content) => setValue(`contents.${index}.content`, content)}
                />
                {errors.contents?.[index]?.content && <span className="text-red-500 text-sm">Content is required</span>}
              </div>
            </div>
          ))}
          <ButtonSecondary
            type="button"
            onClick={() => append({ subtitle: '', content: '' })}
          >
            Add Content Section
          </ButtonSecondary>
        </section>
      </form>
    </div>
  );
};
